import { Button } from "@babackapp/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@babackapp/ui/components/dropdown-menu";
import { Input } from "@babackapp/ui/components/input";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@babackapp/ui/components/toggle-group";
import { type Editor, useEditorState } from "@tiptap/react";
import {
  BoldIcon,
  ChevronDownIcon,
  ImageIcon,
  ItalicIcon,
  Link2Icon,
  PaletteIcon,
  RotateCcw,
  UnderlineIcon,
} from "lucide-react";
import { ChangeEvent, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkModal } from "./link-modal";
import { useBlockPanelPropsContext } from "../../lib/panel/panel-container";

interface EmailPanelToolbarProps {
  editor: Editor | null;
}

// Mirror the backend validation (shop-tracking-notification.controller.ts):
// only jpg/jpeg/png, max 2MB. Enforced client-side so an invalid file gives
// immediate feedback instead of failing the whole template save.
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png"];
const MAX_IMAGE_SIZE_BYTES = 1024 * 1024 * 2;

type ImageWidthUnit = "px" | "%";

// Parse the image node's `width` (e.g. "300px" / "50%") into a value + unit for
// the toolbar inputs. Falls back to an empty px value when unset/unrecognised.
function parseImageWidth(raw?: string | null): {
  value: string;
  unit: ImageWidthUnit;
} {
  const match = /^(\d+(?:\.\d+)?)(px|%)$/.exec(raw ?? "");
  if (match) {
    return { value: match[1], unit: match[2] as ImageWidthUnit };
  }
  return { value: "", unit: "px" };
}

export function EmailPanelToolbar({ editor }: EmailPanelToolbarProps) {
  const { t } = useTranslation("editor-blocks-library");
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const currentColor = editor?.getAttributes("textStyle")?.color || "#000000";

  const { variables, formAPI } = useBlockPanelPropsContext();

  const activeFormats = useMemo(() => {
    if (!editor) return [];

    const formats = [];
    if (editor.isActive("bold")) formats.push("bold");
    if (editor.isActive("italic")) formats.push("italic");
    if (editor.isActive("underline")) formats.push("underline");
    return formats;
  }, [
    editor?.isActive("bold"),
    editor?.isActive("italic"),
    editor?.isActive("underline"),
  ]);

  // Reactively track the image selection so the size controls appear only while
  // an image node is selected (useEditor alone doesn't re-render on selection
  // changes in Tiptap v3).
  const imageState = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isImageSelected: editor?.isActive("image") ?? false,
      width: (editor?.getAttributes("image").width as string | undefined) ?? "",
    }),
  });

  if (!editor) {
    return null;
  }

  const getCurrentFontSize = () => {
    const fontSize = editor.getAttributes("textStyle")?.fontSize || "16px";
    return fontSize;
  };

  const handleFormatChange = (values: string[]) => {
    if (values.includes("bold") && !editor.isActive("bold")) {
      editor.chain().focus().toggleBold().run();
    } else if (!values.includes("bold") && editor.isActive("bold")) {
      editor.chain().focus().toggleBold().run();
    }

    if (values.includes("italic") && !editor.isActive("italic")) {
      editor.chain().focus().toggleItalic().run();
    } else if (!values.includes("italic") && editor.isActive("italic")) {
      editor.chain().focus().toggleItalic().run();
    }

    if (values.includes("underline") && !editor.isActive("underline")) {
      editor.chain().focus().toggleUnderline().run();
    } else if (!values.includes("underline") && editor.isActive("underline")) {
      editor.chain().focus().toggleUnderline().run();
    }
  };

  const handleFontSizeChange = (fontSize: string) => {
    editor.chain().focus().setFontSize(fontSize).run();
  };

  const getFontSizeLabel = () => {
    const fontSize = getCurrentFontSize();
    return fontSize;
  };

  const getCurrentLink = () => {
    if (!editor) return "";
    const attrs = editor.getAttributes("link");
    return attrs.href || "";
  };

  const handleLinkSave = (href: string) => {
    if (href) {
      editor.chain().focus().setLink({ href }).run();
    } else {
      editor.chain().focus().unsetLink().run();
    }
  };

  const handleLinkClick = () => {
    if (editor.isActive("link")) {
      editor.chain().focus().extendMarkRange("link").run();
    }
    setIsLinkModalOpen(true);
  };

  const handleClearColor = () => {
    if (editor) {
      editor.chain().focus().unsetColor().run();
    }
  };

  const handleImageButtonClick = () => {
    imageInputRef.current?.click();
  };

  const handleImageFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const [file] = Array.from(event.target.files ?? []);
    // Allow picking the same file again next time.
    event.target.value = "";
    if (!file || !editor) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setImageError(
        t(
          "editorBlocksLibrary.blocks.paragraph.image.invalidType",
          "Only JPG and PNG images are allowed.",
        ),
      );
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setImageError(
        t(
          "editorBlocksLibrary.blocks.paragraph.image.tooLarge",
          "Image must be under 2 MB.",
        ),
      );
      return;
    }
    setImageError(null);

    // Defer the actual S3 upload to the template save: register the File under a
    // key, insert the image with a local blob: URL for instant preview and the
    // key as `data-upload-key`. At save time the upload pipeline swaps the blob
    // src for the hosted URL (see prepare-upsert-request.ts / backend
    // EmailTemplateImageService). This mirrors the banner block flow and avoids
    // orphaned uploads if the user never saves.
    const uploadKey = crypto.randomUUID();
    formAPI.registerImage(uploadKey, file);
    const previewUrl = URL.createObjectURL(file);
    editor
      .chain()
      .focus()
      .insertContent({
        type: "image",
        attrs: { src: previewUrl, uploadKey },
      })
      .run();
  };

  const isImageSelected = imageState?.isImageSelected ?? false;
  const { value: imageWidthValue, unit: imageWidthUnit } = parseImageWidth(
    imageState?.width,
  );

  const applyImageWidth = (value: string, unit: ImageWidthUnit) => {
    const trimmed = value.trim();
    // Keep the image node selected (no focus()) so these inputs stay visible.
    editor
      .chain()
      .updateAttributes("image", {
        width: trimmed ? `${trimmed}${unit}` : null,
      })
      .run();
  };

  return (
    <div
      className="flex gap-1.5 items-center flex-wrap text-foreground"
      ref={containerRef}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-16 bg-background border shadow justify-between text-xs gap-0"
          >
            {getFontSizeLabel()}
            <ChevronDownIcon className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="bottom"
          align="start"
          className="w-16 text-foreground"
          container={containerRef.current}
        >
          <DropdownMenuItem onClick={() => handleFontSizeChange("12px")}>
            12px
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleFontSizeChange("14px")}>
            14px
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleFontSizeChange("16px")}>
            16px
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleFontSizeChange("18px")}>
            18px
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleFontSizeChange("20px")}>
            20px
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleFontSizeChange("24px")}>
            24px
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ToggleGroup
        variant="default"
        type="multiple"
        value={activeFormats}
        onValueChange={handleFormatChange}
        className="bg-background border shadow h-8"
      >
        <ToggleGroupItem
          value="bold"
          aria-label="Toggle bold"
          className="h-8 w-8 p-0"
        >
          <BoldIcon className="h-3.5 w-3.5" />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="italic"
          aria-label="Toggle italic"
          className="h-8 w-8 p-0"
        >
          <ItalicIcon className="h-3.5 w-3.5" />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="underline"
          aria-label="Toggle underline"
          className="h-8 w-8 p-0"
        >
          <UnderlineIcon className="h-3.5 w-3.5" />
        </ToggleGroupItem>
      </ToggleGroup>
      <Button
        size="sm"
        variant="outline"
        className="bg-background h-8 w-8 p-0"
        onClick={handleLinkClick}
      >
        <Link2Icon className="h-3.5 w-3.5" />
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="bg-background h-8 w-8 p-0"
        onClick={handleImageButtonClick}
        title={t(
          "editorBlocksLibrary.blocks.paragraph.image.insert",
          "Insert image",
        )}
      >
        <ImageIcon className="h-3.5 w-3.5" />
      </Button>
      <input
        ref={imageInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES.join(",")}
        onChange={handleImageFileChange}
        className="hidden"
      />
      <div className="flex bg-background border shadow rounded-md h-8">
        <div className="relative">
          <input
            ref={colorInputRef}
            type="color"
            defaultValue={currentColor}
            onInput={(event) => {
              if (editor) {
                editor
                  .chain()
                  .focus()
                  .setColor(event.currentTarget.value)
                  .run();
              }
            }}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            title="Choose text color"
          />
          <Button
            size="sm"
            variant="ghost"
            className="pointer-events-none rounded-r-none border-r h-8 w-8 p-0"
          >
            <PaletteIcon className="h-3.5 w-3.5" />
          </Button>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="rounded-l-none h-8 w-8 p-0"
          onClick={handleClearColor}
          title="Clear text color"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </Button>
      </div>
      {isImageSelected ? (
        <div className="flex h-8 items-center gap-1 rounded-md border bg-background px-2 shadow">
          <span className="text-xs text-muted-foreground">
            {t("editorBlocksLibrary.blocks.paragraph.image.width", "Width")}
          </span>
          <Input
            type="number"
            min={1}
            value={imageWidthValue}
            placeholder={t(
              "editorBlocksLibrary.blocks.paragraph.image.auto",
              "Auto",
            )}
            onChange={(event) =>
              applyImageWidth(event.target.value, imageWidthUnit)
            }
            className="h-6 w-14 border-0 px-1 text-xs shadow-none focus-visible:ring-0"
          />
          <ToggleGroup
            type="single"
            value={imageWidthUnit}
            onValueChange={(unit) => {
              // Only re-apply when there's a value to convert; otherwise picking
              // a unit on a width-less image would wipe it to null.
              if (unit && imageWidthValue) {
                applyImageWidth(imageWidthValue, unit as ImageWidthUnit);
              }
            }}
            className="h-6"
          >
            <ToggleGroupItem
              value="px"
              aria-label="Width in pixels"
              className="h-6 w-7 p-0 text-xs"
            >
              px
            </ToggleGroupItem>
            <ToggleGroupItem
              value="%"
              aria-label="Width in percent"
              className="h-6 w-7 p-0 text-xs"
            >
              %
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      ) : null}
      <LinkModal
        variables={variables}
        open={isLinkModalOpen}
        onOpenChange={setIsLinkModalOpen}
        currentHref={getCurrentLink()}
        onSave={handleLinkSave}
      />
      {imageError ? (
        <p className="w-full text-sm text-destructive">{imageError}</p>
      ) : null}
    </div>
  );
}
